const SOLARENT_OPERATOR_KIND = 3;

/** Set from RequestAssignment/Status (authoritative). */
let assignmentUserContext = {
  canClaimRequests: false,
  currentUserKind: null,
  currentUserId: null,
};

export const setAssignmentUserContext = (ctx) => {
  assignmentUserContext = { ...assignmentUserContext, ...ctx };
};

export const getAssignmentUserContext = () => ({ ...assignmentUserContext });

const parseJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

export const persistUserIdentityToStorage = ({ userKind, userId }) => {
  try {
    const user = getStoredUser();
    if (!user) return;
    const next = { ...user };
    if (userKind != null && userKind !== "") {
      next.userKind = userKind;
      next.UserKind = userKind;
    }
    if (userId != null && userId !== "") {
      next.id = userId;
      next.Id = userId;
    }
    localStorage.setItem("user", JSON.stringify(next));
  } catch {
    /* ignore */
  }
};

export const getCurrentUserId = () => {
  const ctxId = assignmentUserContext.currentUserId;
  if (ctxId != null && ctxId !== "") return Number(ctxId);

  const user = getStoredUser();
  const direct = user?.id ?? user?.Id ?? user?.userId ?? user?.UserId;
  if (direct != null && direct !== "") return Number(direct);

  const payload = parseJwtPayload(user?.accessToken ?? user?.AccessToken);
  const claim =
    payload?.nameid ??
    payload?.sub ??
    payload?.[
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
    ];
  return claim != null && claim !== "" ? Number(claim) : null;
};

export const getStoredUserKind = () => {
  const ctx = assignmentUserContext.currentUserKind;
  if (ctx != null && ctx !== "") return ctx;

  const user = getStoredUser();
  return (
    user?.userKind ??
    user?.UserKind ??
    user?.roleId ??
    user?.RoleId ??
    null
  );
};

export const isSolarentOperatorUser = () => {
  if (assignmentUserContext.canClaimRequests === true) return true;

  const kind = getStoredUserKind();
  if (kind == null || kind === "") return false;
  if (typeof kind === "number") return kind === SOLARENT_OPERATOR_KIND;
  const normalized = String(kind).trim().toLowerCase();
  return (
    normalized === "solarentoperator" ||
    normalized === "3" ||
    normalized === String(SOLARENT_OPERATOR_KIND)
  );
};

/** API may return camelCase or PascalCase for assignment permission flags. */
export const normalizeAssignmentPermissions = (row) => {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    assignedOperatorUserId:
      row.assignedOperatorUserId ?? row.AssignedOperatorUserId ?? null,
    assignedOperatorFullName:
      row.assignedOperatorFullName ?? row.AssignedOperatorFullName ?? "",
    canClaim: !!(row.canClaim ?? row.CanClaim),
    canRelease: !!(row.canRelease ?? row.CanRelease),
  };
};

const isNotCheckedConfirm = (requestConfirm) =>
  requestConfirm === "NotChecked" ||
  requestConfirm === 0 ||
  requestConfirm === "0";

export const enrichAssignmentRows = (rows = []) => {
  const currentUserId = getCurrentUserId();
  return rows.map((row) => {
    const normalized = normalizeAssignmentPermissions(row);
    const assignedId = normalized.assignedOperatorUserId ?? null;
    const assignedName = normalized.assignedOperatorFullName ?? "";

    let assignedOperatorDisplay = "بدون مسئول";
    let assignedOperatorChipColor = "default";

    if (assignedId != null && assignedId !== "") {
      if (
        currentUserId != null &&
        Number(assignedId) === Number(currentUserId)
      ) {
        assignedOperatorDisplay = "شما";
        assignedOperatorChipColor = "success";
      } else {
        assignedOperatorDisplay = assignedName || "—";
        assignedOperatorChipColor = "default";
      }
    }

    return {
      ...normalized,
      assignedOperatorDisplay,
      assignedOperatorChipColor,
    };
  });
};

export const assignmentOwnerColumn = {
  id: "assignedOperatorDisplay",
  label: "مسئول",
  type: "chip",
  model: { colorField: "assignedOperatorChipColor" },
};

export const ASSIGNMENT_FILTER_OPTIONS_OPERATOR = [
  { value: "mine,unassigned", label: "درخواست‌های من و بدون مسئول" },
  { value: "mine", label: "درخواست‌های من" },
  { value: "unassigned", label: "بدون مسئول" },
  { value: "all", label: "همه" },
];

export const ASSIGNMENT_FILTER_OPTIONS_MANAGEMENT = [
  { value: "all", label: "همه" },
  { value: "mine", label: "درخواست‌های من" },
  { value: "unassigned", label: "بدون مسئول" },
];

export const inferAssignmentFeatureFromRows = (rows = []) =>
  rows.some((row) => {
    const n = normalizeAssignmentPermissions(row);
    return n.canClaim || n.canRelease;
  });

/**
 * Claim in list/modal: server CanClaimRequests + per-row canClaim (or safe fallback).
 */
export const canShowClaimAction = (row, showAssignmentUi = true) => {
  if (!showAssignmentUi || !row || !isSolarentOperatorUser()) return false;

  const n = normalizeAssignmentPermissions(row);
  if (n.canClaim) return true;

  const assignedId = n.assignedOperatorUserId;
  return (
    (assignedId == null || assignedId === "") &&
    isNotCheckedConfirm(row.requestConfirm ?? row.RequestConfirm)
  );
};
